/**
 * Стандартная структура адреса или его части, возвращаемая службой Форматор-Адрес
 */
export interface IFiasAddressPart {
    id: string;
    kladr_id: string;
    level: number;
    shortname: string;
    name: string;
    formalname: string;
    ext1_type: string;
    ext1_name: string;
    ext2_type: string;
    ext2_name: string;
    fullname: string;
    lonlat: string[] | null;
    postalcode: string;
    parents: IFiasAddressPart[] | null;
}

/**
 * Структура значения поля адреса выдаваемая компонентом dynamic-fields/address
 * Используется как значение для полей реестра типа Адрес
 */
export interface IAddress extends IFiasAddressPart {
    additional_information: string;
    search_type: 'administrative' | 'municipal';
    coordinates: string[] | null;
    has_geo: boolean;
}

/**
 * Настройки видимости частей адреса в поле типа "Адрес"
 */
export interface IAddressVisibility {
    /** Флаг отображения поля Код ОКСМ */
    is_oksm?: boolean;

    /** Флаг отображения поля Страна */
    is_country?: boolean;

    /** Флаг отображения поля Индекс */
    is_index?: boolean;

    /** Флаг отображения поля Субъект РФ */
    is_subject?: boolean;

    /** Флаг отображения поля Административного район */
    is_administrative_region?: boolean;

    /** Флаг отображения поля Муниципального район */
    is_municipal_region?: boolean;

    /** Флаг отображения поля Сельское/городское поселение */
    is_urban_settlement?: boolean;

    /** Флаг отображения поля Город */
    is_city?: boolean;

    /** Флаг отображения поля Населенный пункт */
    is_locality?: boolean;

    /** Флаг отображения поля Элемент планировочной структуры */
    is_street?: boolean;

    /** Флаг отображения поля Элемент улично-дорожной сети */
    is_el_road_network?: boolean;

    /** Флаг отображения поля Земельный участок */
    is_land_plot?: boolean;

    /** Флаг отображения поля Здание (сооружение) */
    is_house?: boolean;

    /** Флаг отображения поля Помещение */
    is_apartment?: boolean;

    /** Флаг отображения поля Помещения в пределах помещения */
    is_room?: boolean;

    /** Флаг отображения поля Уровень автономного округа (устаревшее) */
    is_lvl_autonomous_okrug?: boolean;

    /** Флаг отображения поля Уровень внутригородской территории (устаревшее) */
    is_lvl_inner_city_territory?: boolean;

    /** Флаг отображения поля Уровень дополнительных территорий (устаревшее) */
    is_lvl_additional_territories?: boolean;

    /** Флаг отображения поля Уровень объектов на дополнительных территориях (устаревшее) */
    is_lvl_objects_add_territories?: boolean;

    /** Флаг отображения поля Машино-место */
    is_parking_space?: boolean;

    /** Флаг отображения поля Дополнительная информация */
    is_landmark?: boolean;
}

/** Поля, которые отображаются в случае, если видимость полей не задана. */
export const enabledByDefault: (keyof IAddressVisibility)[] = [
    'is_index',
    'is_subject',
    'is_administrative_region',
    'is_municipal_region',
    'is_urban_settlement',
    'is_city',
    'is_locality',
    'is_street',
    'is_el_road_network',
    'is_land_plot',
    'is_house',
    'is_apartment',
    'is_room',
    'is_parking_space',
    'is_landmark',
];

/** Флаги видимости частей адреса, которые не используются на текущий момент, и пока непонятно, что с ними делать */
export const disabledVisibilityParts: (keyof IAddressVisibility)[] = ['is_oksm', 'is_country'];

/**
 * Адреса по уровням
 */
export const fiasAddressLevels = {
    1: 'subject',
    2: 'administrative_region',
    3: 'municipal_region',
    4: 'urban_settlement',
    5: 'city',
    6: 'locality',
    7: 'street',
    8: 'el_road_network',
    9: 'land_plot',
    10: 'house',
    11: 'apartment',
    12: 'room',
    13: 'lvl_autonomous_okrug',
    14: 'lvl_inner_city_territory',
    15: 'lvl_additional_territories',
    16: 'lvl_objects_add_territories',
    17: 'parking_space',
};

/** Индекс этого массива = уровень ФИАС. */
export const fiasLevelToVisibilityKey = (level: number): keyof IAddressVisibility | null => {
    if (level < 1 || level > 17) {
        return null;
    }
    const key = Object.values(fiasAddressLevels)[level - 1];
    return `is_${key}` as keyof IAddressVisibility;
};
